(function () {
  const PODCAST_FALLBACK_FEED = 'https://feed.podbean.com/casualswinger/feed.xml';
  const PARTNER_FALLBACK_FEED = 'https://feed.podbean.com/swingingdownunder/feed.xml';

  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
  const stripHtml = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
  };
  const truncate = (text, max) => {
    const clean = (text || '').trim();
    if (clean.length <= max) return clean;
    return `${clean.slice(0, max).trim()}…`;
  };
  const formatPodcastDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const formatTime = (seconds) => {
    const value = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const h = Math.floor(value / 3600);
    const m = Math.floor((value % 3600) / 60);
    const s = value % 60;
    return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
  };
  const textFrom = (node, selector) => node?.querySelector(selector)?.textContent?.trim() || '';
  const attrFrom = (node, selector, attr) => node?.querySelector(selector)?.getAttribute(attr) || '';
  const getEpisodeImage = (item, feedImage) => attrFrom(item, 'itunes\\:image, image', 'href') || attrFrom(item, 'media\\:thumbnail, thumbnail', 'url') || feedImage || '';

  const parsePodcastXml = (xmlText, sourceUrl) => {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    if (doc.querySelector('parsererror')) throw new Error('Podcast RSS XML could not be parsed');
    const channel = doc.querySelector('channel') || doc;
    const feedImage = attrFrom(channel, 'itunes\\:image, image', 'href') || textFrom(channel, 'image url');
    const feed = {
      title: textFrom(channel, 'title') || 'Podcast',
      description: textFrom(channel, 'itunes\\:summary') || textFrom(channel, 'description'),
      image: feedImage,
      sourceUrl: sourceUrl || ''
    };
    const episodes = Array.from(doc.querySelectorAll('item')).map((item, index) => {
      const enclosure = item.querySelector('enclosure[type^="audio"], enclosure');
      return {
        id: `${feed.title}-${textFrom(item, 'guid') || textFrom(item, 'link') || index}`,
        nativeId: textFrom(item, 'guid') || textFrom(item, 'link') || `episode-${index}`,
        sourceTitle: feed.title,
        sourceUrl: feed.sourceUrl,
        title: textFrom(item, 'title') || 'Podcast episode',
        link: textFrom(item, 'link'),
        pubDate: textFrom(item, 'pubDate'),
        summary: textFrom(item, 'itunes\\:summary') || textFrom(item, 'description'),
        description: textFrom(item, 'content\\:encoded') || textFrom(item, 'description') || textFrom(item, 'itunes\\:summary'),
        duration: textFrom(item, 'itunes\\:duration'),
        audio: enclosure?.getAttribute('url') || '',
        image: getEpisodeImage(item, feedImage)
      };
    }).filter((episode) => episode.title || episode.audio);
    return { feed, episodes };
  };

  const normalizeRss2Json = (payload, sourceUrl) => {
    const feed = { title: payload.feed?.title || 'Podcast', description: payload.feed?.description || '', image: payload.feed?.image || '', sourceUrl: sourceUrl || '' };
    const episodes = (payload.items || []).map((item, index) => ({
      id: `${feed.title}-${item.guid || item.link || index}`,
      nativeId: item.guid || item.link || `episode-${index}`,
      sourceTitle: feed.title,
      sourceUrl: feed.sourceUrl,
      title: item.title || 'Podcast episode',
      link: item.link || '',
      pubDate: item.pubDate || '',
      summary: item.description || item.content || '',
      description: item.content || item.description || '',
      duration: item.itunes_duration || '',
      audio: item.enclosure?.link || item.enclosure?.url || '',
      image: item.thumbnail || item.enclosure?.thumbnail || feed.image || ''
    })).filter((episode) => episode.title || episode.audio);
    return { feed, episodes };
  };

  const fetchText = async (url) => {
    const response = await fetch(url, { headers: { Accept: 'application/rss+xml, application/xml, text/xml, application/json' } });
    if (!response.ok) throw new Error(`Podcast request failed: ${response.status}`);
    return response.text();
  };

  const fetchPodcastPayload = async (feedUrl) => {
    const url = feedUrl || PODCAST_FALLBACK_FEED;
    const encoded = encodeURIComponent(url);
    const attempts = [
      async () => parsePodcastXml(await fetchText(url), url),
      async () => parsePodcastXml(await fetchText(`https://api.allorigins.win/raw?url=${encoded}`), url),
      async () => normalizeRss2Json(JSON.parse(await fetchText(`https://api.rss2json.com/v1/api.json?rss_url=${encoded}`)), url)
    ];
    let lastError;
    for (const attempt of attempts) {
      try {
        const payload = await attempt();
        if (payload.episodes?.length) return payload;
      } catch (error) { lastError = error; }
    }
    throw lastError || new Error('Podcast feed could not be loaded');
  };

  const keywordScore = (active, candidate) => {
    if (!active || !candidate || active.id === candidate.id) return -1;
    const stop = new Set(['the','and','with','from','that','this','your','you','for','our','are','about','episode','podcast','casual','swinger','swingers','lifestyle']);
    const a = `${active.title} ${stripHtml(active.summary)} ${stripHtml(active.description)}`.toLowerCase().match(/[a-z0-9]{4,}/g) || [];
    const c = `${candidate.title} ${stripHtml(candidate.summary)} ${stripHtml(candidate.description)}`.toLowerCase();
    return a.filter((word) => !stop.has(word) && c.includes(word)).length;
  };

  const normalizeSearchText = (value) => String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const searchTokens = (value) => normalizeSearchText(value)
    .split(' ')
    .filter((word) => word.length > 1);

  const splitFeaturedNeedles = (value) => String(value || '')
    .split(/[,;|\n]+/g)
    .map((item) => item.trim())
    .filter(Boolean);

  const readFeaturedConfig = (section, container) => {
    const config = {};
    const configNode = section?.querySelector('[data-podcast-featured-config]');
    if (configNode?.textContent) {
      try { Object.assign(config, JSON.parse(configNode.textContent)); } catch (error) { console.warn('Podcast featured config could not be parsed', error); }
    }
    for (let i = 1; i <= 4; i += 1) {
      config[`featured${i}`] = config[`featured${i}`] || container.dataset[`featured${i}`] || '';
      config[`featured${i}Copy`] = config[`featured${i}Copy`] || container.dataset[`featured${i}Copy`] || '';
    }
    return config;
  };

  const getEpisodeSearchText = (episode) => normalizeSearchText([
    episode.title,
    episode.nativeId,
    episode.id,
    episode.link,
    episode.sourceTitle,
    stripHtml(episode.summary),
    stripHtml(episode.description)
  ].join(' '));

  const episodeMatchScore = (episode, needle) => {
    const query = normalizeSearchText(needle);
    if (!query || !episode) return 0;
    const haystack = getEpisodeSearchText(episode);
    const title = normalizeSearchText(episode.title);
    const nativeId = normalizeSearchText(episode.nativeId);
    const tokens = searchTokens(query);
    let score = 0;

    if (title === query) score += 1000;
    if (title.includes(query)) score += 600;
    if (haystack.includes(query)) score += 280;
    if (nativeId.includes(query)) score += 160;

    tokens.forEach((token) => {
      if (title.split(' ').includes(token)) score += 120;
      else if (title.includes(token)) score += 80;
      if (haystack.includes(token)) score += 35;
    });

    if (tokens.length > 1) {
      const matchedTokens = tokens.filter((token) => haystack.includes(token)).length;
      score += Math.round((matchedTokens / tokens.length) * 220);
    }

    return score;
  };

  const findBestEpisodeMatch = (episodes, needle, usedIds = new Set()) => {
    const needles = splitFeaturedNeedles(needle);
    const queries = needles.length ? needles : [needle];
    const ranked = [];
    queries.forEach((rawQuery) => {
      const query = normalizeSearchText(rawQuery);
      if (!query) return;
      (episodes || []).forEach((episode) => {
        if (!episode || usedIds.has(episode.id)) return;
        const score = episodeMatchScore(episode, query);
        if (score >= 20) ranked.push({ episode, score, query: rawQuery });
      });
    });
    ranked.sort((a, b) => b.score - a.score);
    return ranked[0] || null;
  };

  const renderHomepageMini = async (container) => {
    try {
      const payload = await fetchPodcastPayload(container.dataset.feedUrl || PODCAST_FALLBACK_FEED);
      const episodes = (payload.episodes || []).slice(0, 3);
      container.innerHTML = `<div class="pn-podcast-feed-mini">${episodes.map((episode, index) => `<article class="pn-podcast-feed-mini__item"><p class="pn-card-kicker">${index === 0 ? 'Latest episode' : escapeHtml(formatPodcastDate(episode.pubDate))}</p><h4>${escapeHtml(episode.title)}</h4><p>${escapeHtml(truncate(stripHtml(episode.summary || episode.description), 120))}</p>${episode.link ? `<a class="pn-text-link" href="${escapeHtml(episode.link)}" target="_blank" rel="noopener">Listen now</a>` : ''}</article>`).join('')}</div>`;
    } catch (error) {
      console.error(error);
      container.innerHTML = '<div class="pn-podcast__loading"><strong>Explore the Casual Swinger Podcast</strong><span>Search real conversations about lifestyle travel, community, events, relationships, and stories from the road.</span></div>';
    }
  };

  const initPodcastPlayer = async (container) => {
    const refs = {
      loading: container.querySelector('[data-podcast-player-loading]'),
      error: container.querySelector('[data-podcast-player-error]'),
      now: container.querySelector('[data-podcast-player-now]'),
      list: container.querySelector('[data-podcast-player-list]'),
      count: container.querySelector('[data-podcast-player-count]'),
      search: container.querySelector('[data-podcast-player-search]'),
      art: container.querySelector('[data-podcast-player-art]'),
      meta: container.querySelector('[data-podcast-player-meta]'),
      title: container.querySelector('[data-podcast-player-title]'),
      summary: container.querySelector('[data-podcast-player-summary]'),
      audio: container.querySelector('[data-podcast-player-audio]'),
      play: container.querySelector('[data-podcast-play]'),
      rewind: container.querySelector('[data-podcast-rewind]'),
      forward: container.querySelector('[data-podcast-forward]'),
      speed: container.querySelector('[data-podcast-speed]'),
      scrub: container.querySelector('[data-podcast-scrub]'),
      current: container.querySelector('[data-podcast-current]'),
      duration: container.querySelector('[data-podcast-duration]'),
      link: container.querySelector('[data-podcast-player-link]')
    };
    const section = container.closest('.pn-podcast');
    const featuredPanel = section?.querySelector('[data-podcast-featured-picks]');
    const notesPanel = section?.querySelector('[data-podcast-show-notes]');
    const relatedPanel = section?.querySelector('[data-podcast-related]');
    let ownEpisodes = [];
    let ownFeaturedPool = [];
    let partnerEpisodes = [];
    let episodes = [];
    let filtered = [];
    let activeId = '';

    const renderArtTile = (episode, options = {}) => {
      const source = options.showSource ? `<span class="pn-podcast-art-tile__source">${escapeHtml(episode.sourceTitle || 'Podcast')}</span>` : '';
      const match = options.matchTerm ? `<em class="pn-podcast-art-tile__match">Matched: ${escapeHtml(options.matchTerm)}</em>` : '';
      const copy = options.copy ? `<span class="pn-podcast-art-tile__copy">${escapeHtml(options.copy)}</span>` : '';
      return `<button type="button" class="pn-podcast-pick pn-podcast-art-tile" data-episode-id="${escapeHtml(episode.id)}" data-scroll-to-player="${options.scroll ? 'true' : 'false'}"><span class="pn-podcast-art-tile__image"><img src="${escapeHtml(episode.image || '')}" alt="" loading="lazy">${source}</span><strong>${escapeHtml(episode.title)}</strong>${match}${copy}</button>`;
    };

    const wireEpisodeButtons = (root) => {
      root?.querySelectorAll('[data-episode-id]').forEach((button) => button.addEventListener('click', () => {
        setEpisode(episodes.find((episode) => episode.id === button.dataset.episodeId), button.dataset.scrollToPlayer === 'true');
      }));
    };

    const renderFeatured = () => {
      if (!featuredPanel) return;
      const picks = [];
      const usedIds = new Set();
      const featuredPool = ownFeaturedPool.length ? ownFeaturedPool : ownEpisodes;
      const config = readFeaturedConfig(section, container);
      for (let i = 1; i <= 4; i += 1) {
        const match = config[`featured${i}`] || '';
        const copy = config[`featured${i}Copy`] || '';
        const found = findBestEpisodeMatch(featuredPool, match, usedIds);
        if (found?.episode) {
          usedIds.add(found.episode.id);
          picks.push({ episode: found.episode, copy, matchTerm: found.query || match });
        }
      }
      featuredPool.forEach((episode) => {
        if (picks.length < 4 && !picks.some((item) => item.episode.id === episode.id)) picks.push({ episode, copy: '', matchTerm: '' });
      });
      featuredPanel.innerHTML = picks.slice(0, 4).map((item) => renderArtTile(item.episode, { scroll: true, copy: item.copy, matchTerm: item.matchTerm })).join('');
      wireEpisodeButtons(featuredPanel);
    };

    const renderNotes = (episode) => {
      if (!notesPanel) return;
      const notes = episode?.description || episode?.summary || '';
      notesPanel.innerHTML = `<p class="pn-podcast-source-label">${escapeHtml(episode?.sourceTitle || 'Loaded episode')}</p><h4>${escapeHtml(episode?.title || 'Episode show notes')}</h4><div>${notes ? notes : '<p>No show notes were found in the RSS entry for this episode.</p>'}</div>`;
    };

    const renderRelated = (active) => {
      if (!relatedPanel) return;
      const scoreAndSort = (pool) => pool
        .filter((episode) => episode.id !== active?.id)
        .map((episode) => ({ episode, score: keywordScore(active, episode) }))
        .sort((a, b) => b.score - a.score)
        .map((item) => item.episode);
      const own = scoreAndSort(ownEpisodes);
      const partner = scoreAndSort(partnerEpisodes);
      const selected = [];
      const addUnique = (episode) => { if (episode && !selected.some((item) => item.id === episode.id)) selected.push(episode); };
      addUnique(partner[0]);
      addUnique(own[0]);
      addUnique(partner[1]);
      addUnique(own[1]);
      [...partner, ...own].forEach((episode) => { if (selected.length < 4) addUnique(episode); });
      if (!selected.length) {
        relatedPanel.innerHTML = '<small>No related episode found yet.</small>';
        return;
      }
      relatedPanel.innerHTML = selected.slice(0, 4).map((episode) => renderArtTile(episode, { showSource: true, scroll: true })).join('');
      wireEpisodeButtons(relatedPanel);
    };

    const setEpisode = (episode, shouldScroll = false) => {
      if (!episode) return;
      activeId = episode.id;
      refs.now.hidden = false;
      refs.art.src = episode.image || '';
      refs.art.alt = episode.title || 'Podcast episode artwork';
      refs.meta.textContent = [episode.sourceTitle, formatPodcastDate(episode.pubDate), episode.duration].filter(Boolean).join(' • ') || 'Podcast episode';
      refs.title.textContent = episode.title || 'Podcast episode';
      refs.summary.textContent = truncate(stripHtml(episode.summary || episode.description), 240);
      refs.audio.src = episode.audio || '';
      refs.audio.playbackRate = Number(refs.speed.value || 1);
      refs.play.textContent = 'Play';
      refs.scrub.value = 0;
      refs.current.textContent = '0:00';
      refs.duration.textContent = episode.duration || '0:00';
      refs.link.href = episode.link || episode.audio || '#';
      refs.link.hidden = !(episode.link || episode.audio);
      renderNotes(episode);
      renderRelated(episode);
      renderList();
      if (shouldScroll) {
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        refs.now.focus?.({ preventScroll: true });
      }
    };

    const renderList = () => {
      const term = (refs.search.value || '').toLowerCase().trim();
      filtered = episodes.filter((episode) => !term || [episode.sourceTitle, episode.title, stripHtml(episode.summary), stripHtml(episode.description), formatPodcastDate(episode.pubDate)].join(' ').toLowerCase().includes(term));
      refs.list.innerHTML = filtered.map((episode) => `<button type="button" class="pn-podcast-tile${episode.id === activeId ? ' is-active' : ''}" data-episode-id="${escapeHtml(episode.id)}"><img src="${escapeHtml(episode.image || '')}" alt="" loading="lazy"><span><small>${escapeHtml([episode.sourceTitle, formatPodcastDate(episode.pubDate)].filter(Boolean).join(' • '))}</small><strong>${escapeHtml(episode.title)}</strong><em>${escapeHtml(truncate(stripHtml(episode.summary || episode.description), 92))}</em></span></button>`).join('');
      refs.list.hidden = false;
      refs.count.hidden = false;
      refs.count.textContent = term ? `${filtered.length} matching episode${filtered.length === 1 ? '' : 's'}` : `${episodes.length} episodes loaded`;
      wireEpisodeButtons(refs.list);
    };

    refs.play.addEventListener('click', async () => {
      if (!refs.audio.src) return;
      if (refs.audio.paused) {
        try { await refs.audio.play(); } catch (error) { console.error(error); }
      } else { refs.audio.pause(); }
    });
    refs.audio.addEventListener('play', () => { refs.play.textContent = 'Pause'; });
    refs.audio.addEventListener('pause', () => { refs.play.textContent = 'Play'; });
    refs.audio.addEventListener('loadedmetadata', () => { refs.duration.textContent = formatTime(refs.audio.duration); });
    refs.audio.addEventListener('timeupdate', () => {
      refs.current.textContent = formatTime(refs.audio.currentTime);
      if (refs.audio.duration) refs.scrub.value = String((refs.audio.currentTime / refs.audio.duration) * 100);
    });
    refs.scrub.addEventListener('input', () => { if (refs.audio.duration) refs.audio.currentTime = (Number(refs.scrub.value) / 100) * refs.audio.duration; });
    refs.rewind.addEventListener('click', () => { refs.audio.currentTime = Math.max(0, refs.audio.currentTime - 15); });
    refs.forward.addEventListener('click', () => { refs.audio.currentTime = Math.min(refs.audio.duration || refs.audio.currentTime + 30, refs.audio.currentTime + 30); });
    refs.speed.addEventListener('change', () => { refs.audio.playbackRate = Number(refs.speed.value || 1); });
    refs.search.addEventListener('input', renderList);

    try {
      const limit = parseInt(container.dataset.episodeLimit || '18', 10);
      const ownPayload = await fetchPodcastPayload(container.dataset.feedUrl || PODCAST_FALLBACK_FEED);
      ownFeaturedPool = ownPayload.episodes || [];
      // Load the full Casual Swinger library for search, featured picks, and the episode rail.
      // The old code sliced this to the visible tile limit, which capped the library at 18 own episodes
      // plus a small partner batch. That is why the player kept reporting only 27 loaded.
      ownEpisodes = ownFeaturedPool;
      try {
        const partnerPayload = await fetchPodcastPayload(container.dataset.partnerFeedUrl || PARTNER_FALLBACK_FEED);
        partnerEpisodes = (partnerPayload.episodes || []).slice(0, Math.max(8, Math.floor(limit / 2)));
      } catch (partnerError) {
        console.warn('Partner podcast feed unavailable', partnerError);
        partnerEpisodes = [];
      }
      episodes = [...ownEpisodes, ...partnerEpisodes];
      if (!episodes.length) throw new Error('No podcast episodes found');
      refs.loading?.remove();
      renderFeatured();
      setEpisode(ownEpisodes[0] || episodes[0]);
    } catch (error) {
      console.error(error);
      refs.loading?.remove();
      refs.error.hidden = false;
      refs.error.innerHTML = '<strong>Explore the Casual Swinger Podcast</strong><span>Real conversations, lifestyle travel stories, community updates, and episodes about connection, events, and adventures.</span>';
      if (featuredPanel) featuredPanel.innerHTML = '<small>Episode picks need the podcast feed to load first.</small>';
      if (relatedPanel) relatedPanel.innerHTML = '<small>Related listening needs the podcast feed to load first.</small>';
    }
  };

  window.fetchPodcastPayload = fetchPodcastPayload;
  window.stripHtml = stripHtml;
  window.truncate = truncate;
  window.formatPodcastDate = formatPodcastDate;

  const initAllPodcastPlayers = () => {
    document.querySelectorAll('[data-podcast-rss]:not([data-podcast-initialized])').forEach((el) => { el.dataset.podcastInitialized = 'true'; renderHomepageMini(el); });
    document.querySelectorAll('[data-podcast-player]:not([data-podcast-initialized])').forEach((el) => { el.dataset.podcastInitialized = 'true'; initPodcastPlayer(el); });
  };

  document.addEventListener('DOMContentLoaded', initAllPodcastPlayers);
  document.addEventListener('shopify:section:load', (event) => {
    event.target?.querySelectorAll?.('[data-podcast-initialized]').forEach((el) => { delete el.dataset.podcastInitialized; });
    initAllPodcastPlayers();
  });
})();
